export interface AtonDataPoint {
  timestamp: string; // ISO string for compatibility with ship chart
  value: number;
  index: number;
}

export interface AtonChartData {
  title: string;
  hostName: string;
  serviceName: string;
  data: AtonDataPoint[];
  timeRange: {
    start: Date;
    end: Date;
    step: number;
  };
}

// Updated parser function
export function parseAtonGraphData(htmlResponse: string): AtonChartData | null {
  try {
    // Find the create_graph function call
    const createGraphIndex = htmlResponse.indexOf('cmk.graphs.create_graph(');
    if (createGraphIndex === -1) {
      console.error('Could not find create_graph function');
      return null;
    }

    // Extract everything after create_graph(
    const afterFunction = htmlResponse.substring(
      createGraphIndex + 'cmk.graphs.create_graph('.length
    );

    // Find the first comma (after the HTML string parameter)
    const firstCommaIndex = afterFunction.indexOf(',');
    if (firstCommaIndex === -1) {
      console.error('Could not find first parameter comma');
      return null;
    }

    // Extract everything after the first comma and trim
    const afterFirstParam = afterFunction.substring(firstCommaIndex + 1).trim();

    // The JSON object starts with { and we need to find its matching }
    const startIndex = afterFirstParam.indexOf('{');
    if (startIndex === -1) {
      console.error('Could not find JSON start');
      return null;
    }

    // Count braces to find the complete JSON object
    let braceCount = 0;
    let endIndex = -1;

    for (let i = startIndex; i < afterFirstParam.length; i++) {
      if (afterFirstParam[i] === '{') {
        braceCount++;
      } else if (afterFirstParam[i] === '}') {
        braceCount--;
        if (braceCount === 0) {
          endIndex = i;
          break;
        }
      }
    }

    if (endIndex === -1) {
      console.error('Could not find JSON end');
      return null;
    }

    // Extract the complete JSON string
    const jsonString = afterFirstParam.substring(startIndex, endIndex + 1);

    // Parse the JSON
    const graphData = JSON.parse(jsonString);
    // Extract the data we need
    const title = graphData.title || 'AtoN Graph';
    const startTime = graphData.start_time;
    const endTime = graphData.end_time;
    const step = graphData.step;

    // Create Date objects for better timezone handling
    const startDate = new Date(startTime * 1000);
    const endDate = new Date(endTime * 1000);

    // Get host and service from the definition
    const hostName =
      graphData.definition?.specification?.host_name || 'Unknown';
    const serviceName =
      graphData.definition?.specification?.service_description || 'Unknown';

    // Extract data points from the first curve
    const dataPoints: AtonDataPoint[] = [];

    if (graphData.curves && graphData.curves.length > 0) {
      const curve = graphData.curves[0];
      if (curve.points && Array.isArray(curve.points)) {
        curve.points.forEach((point: [number, number], index: number) => {
          if (Array.isArray(point) && point.length >= 2 && point[1] !== null) {
            const timestamp = startTime + index * step;
            const value = point[1]; // Second element is the value

            if (typeof value === 'number' && !isNaN(value)) {
              // Convert to Date object, then to ISO string for chart compatibility
              const pointDate = new Date(timestamp * 1000);
              dataPoints.push({
                timestamp: pointDate.toISOString(),
                value,
                index,
              });
            }
          }
        });
      }
    }

    return {
      title,
      hostName,
      serviceName,
      data: dataPoints,
      timeRange: {
        start: startDate,
        end: endDate,
        step: step,
      },
    };
  } catch (error) {
    console.error('Error parsing AtoN graph data:', error);
    return null;
  }
}
